import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import Modal from '../../common/Modal';
import ModalAction from '../../common/ModalAction';
import VideoTimeline from '../../common/VideoTimeline';
import RangeSlider from '../../common/RangeSlider';
import useYouTubePlayer from '../../../hooks/useYouTubePlayer';
import useSpaceToPlay from '../../../hooks/useSpaceToPlay';
import useArrowsToSkip from '../../../hooks/useArrowsToSkip';
import useLoopKeys, { LOOP_KEYS } from '../../../hooks/useLoopKeys';
import {
  MAX_UNATTENDED_LOOPS,
  NUDGE_SECONDS,
  clampRange,
  formatRate,
  formatTime,
  practiceRates,
  rateIndex,
  rateTone,
  shouldLoopBack,
} from '../../../utils/youtubePractice';
import { hasFinePointer } from '../../../utils/platform';
import { ReactComponent as PlayIcon } from '../../../icons/play-solid.svg';
import { ReactComponent as PauseIcon } from '../../../icons/pause-solid.svg';
import { ReactComponent as MinusIcon } from '../../../icons/minus.svg';
import { ReactComponent as PlusIcon } from '../../../icons/plus.svg';
import { ReactComponent as YouTubeIcon } from '../../../icons/youtube.svg';

import './PracticeVideoModal.scss';

const SKIP_SECONDS = [10, 5];
// What the arrow keys skip by, alone and with Shift, and the least time their button shows pressed
const ARROW_SKIP_SECONDS = 10;
const SHIFT_ARROW_SKIP_SECONDS = 5;
const MIN_PRESS_MS = 150;

// A skip button's tooltip, naming its key. A touch screen has no keys to name, so it gets none
const skipTitle = (t, direction, secs) => {
  const arrow = direction === 'back' ? '←' : '→';
  const shortcut = { [ARROW_SKIP_SECONDS]: arrow, [SHIFT_ARROW_SKIP_SECONDS]: `⇧${arrow}` }[secs];
  if (!hasFinePointer || !shortcut) return undefined;
  return t(`dialogs.practiceVideo.${direction}Shortcut`, { count: secs, shortcut });
};

/** A song's YouTube video with an A–B loop and speed control, for practicing a stretch of it */
const PracticeVideoModal = ({ videoId, youtubeUrl, onClose }) => {
  const { t } = useTranslation();
  // null until set: the loop's end then follows the video's length, which isn't known at first
  const [range, setRange] = useState({ a: 0, b: null });
  const [looping, setLooping] = useState(false);
  // Loops gone round since anyone last touched a control
  const loopsRef = useRef(0);
  const [askingStillThere, setAskingStillThere] = useState(false);

  const { mountRef, ready, failed, playing, time, duration, rate, rates, play, pause, seek, setRate, currentTime } =
    useYouTubePlayer(videoId, {
      onTick: (now) => {
        if (looping && shouldLoopBack(now, range.a, range.b ?? duration)) loopBack();
      },
      // A loop that runs to the very end can miss its last tick
      onEnded: () => {
        if (looping && loopBack()) play();
      },
    });

  const loopEnd = range.b ?? duration;

  // The slider moves through the speeds by place in the list, so each step is one speed, evenly spaced
  const speeds = practiceRates(rates);
  const speedIndex = rateIndex(speeds, rate);
  const speedMarks = speeds.map((r, i) => ({ value: i, major: r === 1 }));

  // Back to the start, unless it has gone round MAX_UNATTENDED_LOOPS times untouched: then it pauses
  // and asks, so a loop left running doesn't play all night. Says whether it carried on.
  function loopBack() {
    seek(range.a);
    loopsRef.current += 1;
    if (loopsRef.current < MAX_UNATTENDED_LOOPS) return true;

    pause();
    setAskingStillThere(true);
    return false;
  }

  // Any touch of a control is someone being there
  const resetLoopCount = () => {
    loopsRef.current = 0;
    setAskingStillThere(false);
  };

  const handleStillThere = () => {
    resetLoopCount();
    play();
  };

  const updateRange = (a, b, edge) => {
    setRange(clampRange(a, b, duration, edge));
    resetLoopCount();
  };

  const setPoint = (edge, seconds) => {
    if (edge === 'a') updateRange(seconds, loopEnd, 'a');
    else updateRange(range.a, seconds, 'b');
    // Marking a point is asking for the loop
    setLooping(true);
  };

  const handlePlay = () => {
    resetLoopCount();
    const now = currentTime();
    if (looping && (now < range.a || now >= loopEnd)) seek(range.a);
    play();
  };

  const togglePlay = () => (playing ? pause() : handlePlay());

  const skip = (secs) => seek(Math.min(Math.max(currentTime() + secs, 0), duration || Infinity));

  // After "Set" the focus stays on it, so Space has to play rather than press it again
  useSpaceToPlay(ready ? togglePlay : null);

  // The button a key is holding down, as `back-10`, `set-a` or `clear`, pressed in as if tapped
  const [keyPressed, setKeyPressed] = useState(null);
  const pressedAtRef = useRef(0);
  const releaseTimerRef = useRef(null);
  useEffect(() => () => clearTimeout(releaseTimerRef.current), []);

  const pressKey = (button) => {
    clearTimeout(releaseTimerRef.current);
    pressedAtRef.current = Date.now();
    setKeyPressed(button);
  };

  const releaseKey = () => {
    const left = MIN_PRESS_MS - (Date.now() - pressedAtRef.current);
    releaseTimerRef.current = setTimeout(() => setKeyPressed(null), Math.max(left, 0));
  };

  const arrowSkip = (secs) => {
    skip(secs);
    pressKey(`${secs < 0 ? 'back' : 'forward'}-${Math.abs(secs)}`);
  };

  useArrowsToSkip(ready ? arrowSkip : null, {
    seconds: ARROW_SKIP_SECONDS,
    shiftSeconds: SHIFT_ARROW_SKIP_SECONDS,
    onRelease: releaseKey,
  });

  const markPoint = (edge) => {
    setPoint(edge, currentTime());
    pressKey(`set-${edge}`);
  };

  // Back to the whole video with the loop off, as it opened; the video carries on from where it is
  const clearLoop = () => {
    setRange({ a: 0, b: null });
    setLooping(false);
    resetLoopCount();
  };

  // Back to the loop's start and playing, to go again from the top; says whether there was a loop
  const restartLoop = () => {
    if (!looping) return false;
    resetLoopCount();
    seek(range.a);
    play();
    return true;
  };

  const clearFromKey = () => {
    if (!looping) return;
    clearLoop();
    pressKey('clear');
  };

  useLoopKeys(ready ? { mark: markPoint, clear: clearFromKey, restart: restartLoop } : null, releaseKey);

  const renderPoint = (edge) => {
    const value = edge === 'a' ? range.a : loopEnd;
    const name = t(`dialogs.practiceVideo.${edge === 'a' ? 'start' : 'end'}`);

    return (
      <div className={cx('point', `point-${edge}`)}>
        <span className="point-name">{name}</span>
        <span className="point-time">{formatTime(value)}</span>
        <button
          type="button"
          className="nudge"
          aria-label={t('dialogs.practiceVideo.earlier', { point: name })}
          onClick={() => setPoint(edge, value - NUDGE_SECONDS)}
          disabled={!ready}
        >
          <MinusIcon />
        </button>
        <button
          type="button"
          className="nudge"
          aria-label={t('dialogs.practiceVideo.later', { point: name })}
          onClick={() => setPoint(edge, value + NUDGE_SECONDS)}
          disabled={!ready}
        >
          <PlusIcon />
        </button>
        <button
          type="button"
          className={cx('set-here', { 'is-pressed': keyPressed === `set-${edge}` })}
          title={hasFinePointer ? t('dialogs.practiceVideo.setHereShortcut', { shortcut: LOOP_KEYS[edge] }) : undefined}
          onClick={() => setPoint(edge, currentTime())}
          disabled={!ready}
        >
          {t('dialogs.practiceVideo.setHere')}
        </button>
      </div>
    );
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('dialogs.practiceVideo.title')}
      className="PracticeVideoModal"
      actions={
        youtubeUrl && (
          // Pauses here, so the two don't play over each other
          <ModalAction
            icon={<YouTubeIcon />}
            label={t('dialogs.practiceVideo.openInYouTube')}
            href={youtubeUrl}
            onClick={pause}
            showLabel
          />
        )
      }
    >
      <div className="columns">
        <div className="stage">
          <div className="player-frame">
            <div className="player" ref={mountRef} />
            {askingStillThere && (
              <div className="still-there" role="alertdialog" aria-labelledby="practice-still-there">
                <p id="practice-still-there" className="still-there-question">
                  {t('dialogs.practiceVideo.stillThere')}
                </p>
                <p className="still-there-reason">
                  {t('dialogs.practiceVideo.stillTherePaused', { count: MAX_UNATTENDED_LOOPS })}
                </p>
                <button type="button" className="still-there-yes" onClick={handleStillThere}>
                  {t('dialogs.practiceVideo.stillThereYes')}
                </button>
              </div>
            )}
            {!ready && (
              <div className="player-status">
                {failed ? t('dialogs.practiceVideo.failed') : t('dialogs.practiceVideo.loading')}
              </div>
            )}
          </div>

          <VideoTimeline
            time={time}
            duration={duration}
            onSeek={seek}
            range={looping ? { a: range.a, b: loopEnd } : null}
            label={t('dialogs.practiceVideo.seek')}
          />

          <div className="transport">
            {SKIP_SECONDS.map((secs) => (
              <button
                key={`back-${secs}`}
                type="button"
                className={cx('skip', { 'is-pressed': keyPressed === `back-${secs}` })}
                aria-label={t('dialogs.practiceVideo.back', { count: secs })}
                title={skipTitle(t, 'back', secs)}
                onClick={() => skip(-secs)}
                disabled={!ready}
              >
                −{secs}s
              </button>
            ))}
            <button
              type="button"
              className="play-pause"
              aria-label={t(`dialogs.practiceVideo.${playing ? 'pause' : 'play'}`)}
              title={hasFinePointer ? t(`dialogs.practiceVideo.${playing ? 'pause' : 'play'}Shortcut`) : undefined}
              onClick={togglePlay}
              disabled={!ready}
            >
              {playing ? <PauseIcon /> : <PlayIcon />}
            </button>
            {[...SKIP_SECONDS].reverse().map((secs) => (
              <button
                key={`forward-${secs}`}
                type="button"
                className={cx('skip', { 'is-pressed': keyPressed === `forward-${secs}` })}
                aria-label={t('dialogs.practiceVideo.forward', { count: secs })}
                title={skipTitle(t, 'forward', secs)}
                onClick={() => skip(secs)}
                disabled={!ready}
              >
                +{secs}s
              </button>
            ))}
          </div>
        </div>
        <div className="controls">
          <div className="loop">
            <div className="loop-header">
              <span className="loop-name">{t('dialogs.practiceVideo.loop')}</span>
              <button
                type="button"
                className={cx('clear-loop', { 'is-pressed': keyPressed === 'clear' })}
                title={
                  hasFinePointer ? t('dialogs.practiceVideo.clearShortcut', { shortcut: LOOP_KEYS.clear }) : undefined
                }
                onClick={clearLoop}
                disabled={!ready || !looping}
              >
                {t('common.clear')}
              </button>
            </div>
            {renderPoint('a')}
            {renderPoint('b')}
          </div>

          <div className="speed">
            <span className="speed-name">{t('dialogs.practiceVideo.speed')}</span>
            {/* Keyed on the list, which only arrives once the player is ready: a fresh slider then mounts
                in place rather than sliding over from where the stand-in list had it */}
            <RangeSlider
              key={speeds.join()}
              id="practice-speed"
              className={cx('speed-slider', `is-${rateTone(speeds[speedIndex])}`)}
              ariaLabel={t('dialogs.practiceVideo.speed')}
              valueText={formatRate(speeds[speedIndex])}
              value={speedIndex}
              onChange={(e) => setRate(speeds[Number(e.target.value)])}
              min={0}
              max={speeds.length - 1}
              step={1}
              marks={speedMarks}
              disabled={!ready}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

PracticeVideoModal.propTypes = {
  videoId: PropTypes.string.isRequired,
  // The link as the song has it, for the Open in YouTube button; without it there's no button
  youtubeUrl: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default PracticeVideoModal;
